import { SystemStatsSchema, type SystemStats } from '@digger/shared';

const POLL_MS = 2000;
const STORAGE_KEY = 'digger.systemPanel.expanded';

/**
 * Expandable Pi telemetry panel, docked under the HUD. Two sources:
 *  - 'poll' (LAN): fetches /api/system while expanded.
 *  - 'push' (relay): the agent streams stats over signaling because the
 *    console can't reach the Pi's HTTP endpoints — feed() them in.
 * Undervoltage/throttling is surfaced on the header even when collapsed —
 * a browning-out Pi is an operator problem (video and control die with
 * it), not a log line.
 */
export class SystemPanel {
  private readonly header: HTMLButtonElement;
  private readonly body: HTMLElement;
  private expanded = false;
  private timer: number | undefined;

  constructor(
    container: HTMLElement,
    private readonly source: 'poll' | 'push' = 'poll',
  ) {
    container.innerHTML = `
      <button type="button" data-sys="toggle">▸ pi system</button>
      <div data-sys="body" hidden></div>`;
    this.header = container.querySelector('[data-sys="toggle"]')!;
    this.body = container.querySelector('[data-sys="body"]')!;

    this.header.addEventListener('click', () => {
      this.setExpanded(!this.expanded);
      // Never leave a button focused: Space is E-STOP, and a focused
      // control would swallow it as a click.
      this.header.blur();
    });

    if (localStorage.getItem(STORAGE_KEY) === 'true') this.setExpanded(true);
  }

  setExpanded(expanded: boolean): void {
    this.expanded = expanded;
    this.body.hidden = !expanded;
    this.header.textContent = `${expanded ? '▾' : '▸'} pi system`;
    localStorage.setItem(STORAGE_KEY, String(expanded));
    if (this.source !== 'poll') return; // push mode: render on feed()
    if (expanded) {
      void this.poll();
      this.timer = window.setInterval(() => void this.poll(), POLL_MS);
    } else if (this.timer !== undefined) {
      clearInterval(this.timer);
      this.timer = undefined;
    }
  }

  get isExpanded(): boolean {
    return this.expanded;
  }

  /** Push-mode input (stats arriving over signaling). */
  feed(stats: SystemStats): void {
    this.render(stats);
  }

  private async poll(): Promise<void> {
    try {
      const res = await fetch('/api/system');
      const stats = SystemStatsSchema.parse(await res.json());
      this.render(stats);
    } catch {
      this.body.innerHTML = `<div class="sys-row">telemetry unavailable</div>`;
    }
  }

  render(stats: SystemStats): void {
    const { cpu, memory, power } = stats;
    const alerts = throttleAlerts(power.throttled);
    this.header.textContent =
      `${this.expanded ? '▾' : '▸'} pi system${alerts.length ? ` ⚠ ${alerts[0]}` : ''}`;
    this.header.dataset.alert = String(alerts.length > 0);

    const rows: Array<[string, string, 'ok' | 'warn' | 'bad' | undefined]> = [
      ['cpu', `${fmt(cpu.percent, '%')} · load ${cpu.loadAvg1} / ${cpu.cores}`, level(cpu.percent, 70, 90)],
      ['clock', fmt(cpu.clockMhz, ' MHz'), undefined],
      ['temp', fmt(cpu.tempC, '°C'), level(cpu.tempC, 70, 80)],
      ['memory', `${memory.usedMb} / ${memory.totalMb} MB`, level((memory.usedMb / memory.totalMb) * 100, 80, 92)],
      ['power', fmt(power.watts, ' W'), undefined],
      ['uptime', formatUptime(stats.uptimeSec), undefined],
    ];
    for (const alert of alerts) rows.push(['alert', alert, 'bad']);

    this.body.innerHTML = rows
      .map(
        ([label, value, lvl]) =>
          `<div class="sys-row"${lvl ? ` data-level="${lvl}"` : ''}><span>${label}</span><span>${value}</span></div>`,
      )
      .join('');
  }
}

export function throttleAlerts(t: SystemStats['power']['throttled']): string[] {
  if (!t) return [];
  const alerts: string[] = [];
  if (t.undervoltageNow) alerts.push('UNDERVOLTAGE');
  if (t.throttledNow) alerts.push('CPU THROTTLED');
  if (t.cappedNow) alerts.push('freq capped');
  if (t.softTempLimitNow) alerts.push('soft temp limit');
  if (alerts.length === 0 && (t.undervoltageEver || t.throttledEver)) {
    alerts.push('brownout since boot');
  }
  return alerts;
}

const fmt = (v: number | null, unit: string): string => (v === null ? '—' : `${v}${unit}`);

function level(v: number | null, warn: number, bad: number): 'ok' | 'warn' | 'bad' | undefined {
  if (v === null) return undefined;
  return v >= bad ? 'bad' : v >= warn ? 'warn' : 'ok';
}

export function formatUptime(sec: number): string {
  const d = Math.floor(sec / 86400);
  const h = Math.floor((sec % 86400) / 3600);
  const m = Math.floor((sec % 3600) / 60);
  return d > 0 ? `${d}d ${h}h ${m}m` : h > 0 ? `${h}h ${m}m` : `${m}m`;
}
